import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Response } from "../types";

import { CreateInputVariableSetRequest, InputVariableSet } from "./types";

import { jsonAuthHeaders } from "@/utils/headers";
import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums/toast";

export const useCreateInputVariableSet = (userToken: string) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();

  return useMutation<
    Response<InputVariableSet, "input_variable_set", string>,
    Error,
    CreateInputVariableSetRequest
  >({
    mutationFn: async (data: CreateInputVariableSetRequest) => {
      const response = await fetch(ENDPOINTS.input_variable_sets(Number(workspaceId)), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(data),
      });

      return handleResponse(response);
    },
    onSuccess: args => {
      displayToast("Input variable set created successfully", ToastType.success);
      queryClient.invalidateQueries(["input-variable-sets", workspaceId]);
      queryClient.invalidateQueries({
        queryKey: ["folder-entities", workspaceId, null, undefined, undefined, true],
      });
      queryClient.invalidateQueries({
        queryKey: ["folder-entities", workspaceId, args?.input_variable_set?.folder_id ?? null],
      });
    },
    onError: error => {
      displayErrorToast(typeof error === "string" ? error : "Failed to create input variable set");
    },
  });
};
